import './government-incentives.scss';

import React from 'react';
import { PageProps, graphql } from 'gatsby';
import { useTranslation } from 'react-i18next';

import { SEOArguments } from '../classes/seoArguments';
import { usePageLocalization } from '@sbi-international/boutik.gatsby.plugin.multilocale';
import SEO from '../components/Seo';
import MainLayout from '../components/layout/MainLayout';
import { LocalType } from '../models/localType';

type Incentive = {
    isCurrentCountry: boolean;
    title: string;
    fileUrl: string;
    url: string;
};

export const query = graphql`
    query GovernmentIncentivesPage {
        incentives: allProductsJson(
            filter: { category: { eq: "/Century/CMS/Government incentives/" } }
        ) {
            nodes {
                category
                code
                title {
                    en_CA
                    en_US
                    fr_CA
                }
                documents {
                    en_CA
                    en_US
                    fr_CA
                }
                attributes {
                    available_country {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    url {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                }
            }
        }
        metadata: allProductsJson(
            filter: { code: { eq: "century_incitatifs-gov" } }
        ) {
            nodes {
                code
                attributes {
                    meta_title {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    meta_description {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                }
            }
        }
    }
`;

export default function GovernmentIncentives({
    data,
}: PageProps<Queries.GovernmentIncentivesPageQuery>) {
    const { t } = useTranslation('pages');
    const { currentLocale } = usePageLocalization();
    const incentives = data.incentives.nodes;

    const seoArguments = new SEOArguments(
        data.metadata,
        currentLocale as keyof LocalType
    );

    const incentiveFactory = (incentive: any): Incentive => {
        const currentCountry =
            currentLocale.split('_')[1] === 'CA' ? 'Canada' : 'United States';
        const country =
            incentive?.attributes?.available_country?.values?.[0]?.en_CA;

        return {
            isCurrentCountry: currentCountry == country,
            title: incentive?.title?.[currentLocale],
            fileUrl: `/file/${incentive?.documents?.[0]?.[currentLocale]}`,
            url: incentive?.attributes?.url?.values?.[0]?.[
                currentLocale
            ]?.trim(),
        };
    };

    return (
        <MainLayout>
            <SEO
                title={seoArguments.title}
                description={seoArguments.description}
            />
            <div className='container gov-incentives'>
                <h1>{t('gov-incentives-title')}</h1>
                <h4>{t('gov-incentives-subtitle')}</h4>
                <ul>
                    {incentives.map((incentive: Queries.GovernmentIncentivesPageQuery['incentives']['nodes'][0], index) => {
                        const formattedIncentive = incentiveFactory(incentive);

                        return formattedIncentive.isCurrentCountry ? (
                            <li key={`curren_country${index}`}>
                                <a
                                    href={
                                        formattedIncentive.url
                                            ? formattedIncentive.url
                                            : formattedIncentive.fileUrl
                                    }
                                    target='_blank'
                                    rel='noreferrer'
                                    title={formattedIncentive.title}
                                >
                                    {formattedIncentive.title}
                                </a>
                            </li>
                        ) : undefined;
                    })}
                </ul>
            </div>
        </MainLayout>
    );
}
